import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { format, parseISO } from "date-fns";
import { Plane, Trash2, ExternalLink, Ticket } from "lucide-react";
import { bookingsApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import type { Booking } from "@/lib/types";

export default function BookedTrips() {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["bookings"],
    queryFn: bookingsApi.getAll,
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => bookingsApi.delete(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["bookings"] }),
  });

  const bookings: Booking[] = data?.bookings || [];

  if (isLoading) {
    return (
      <div className="space-y-4">
        <h1 className="text-2xl font-bold">Locked In</h1>
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-24 w-full" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Ticket className="h-6 w-6 text-primary" /> Locked In
        </h1>
        <Badge variant="secondary">{bookings.length} booking{bookings.length !== 1 ? "s" : ""}</Badge>
      </div>

      {bookings.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            <Plane className="h-12 w-12 mx-auto mb-3 opacity-50" />
            <p className="text-lg font-medium mb-1">No flights locked in yet</p>
            <p className="text-sm">When you lock in flights from your results, they appear here. Gains secured!</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {bookings.map((booking: Booking) => (
            <Card key={booking.id}>
              <CardContent className="py-4 px-5">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div className="min-w-0">
                    <p className="font-medium">{booking.airline || "Unknown Airline"} {booking.flight_number || ""}</p>
                    <p className="text-sm text-muted-foreground mt-1">
                      {booking.origin || "---"} &rarr; {booking.destination || "---"}
                    </p>
                    {booking.departure_time && (
                      <p className="text-xs text-muted-foreground mt-1">
                        {format(parseISO(booking.departure_time), "MMM d, yyyy 'at' h:mm a")}
                      </p>
                    )}
                    {booking.trip_name && (
                      <Badge variant="outline" className="mt-2 text-xs">{booking.trip_name}</Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    {booking.price_at_booking !== undefined && booking.price_at_booking !== null && (
                      <p className="text-lg font-bold">${Math.round(booking.price_at_booking)}</p>
                    )}
                    {booking.confirmation_code && (
                      <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200">
                        {booking.confirmation_code}
                      </Badge>
                    )}
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-destructive hover:text-destructive"
                      onClick={() => deleteMutation.mutate(booking.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                {booking.notes && (
                  <p className="text-sm text-muted-foreground mt-2 border-t pt-2">{booking.notes}</p>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
